import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import { calculateCostingTodoService } from '../../../../services/todo-list.service';
import type { CostingCalcProjection, CostingEditPayload } from './costing-todo-editing';
import type { CostingSaveStatus } from './store/Costing.type';

/* Save-on-blur for inline Costing TODO edits (BM-9220).

   The request carries the EDITS a user made, not the costing document: the calculation runs on the
   server, and the response is what the grid repaints from. That is what lets the list payload stop
   carrying the calculation inputs for every row.

   Edits queue per costing and are sent as one batch when focus leaves a cell, so tabbing across
   several cells of one costing produces a couple of requests rather than one per cell. One request
   is in flight per costing at a time; edits made while it runs are held and flushed after. */

/** How long after focus leaves a cell the pending edits are flushed, in milliseconds. */
const BLUR_FLUSH_DELAY = 0;

interface SaveOutcome {
  saved?: Array<{ _id: string }>;
  failed?: Array<{ _id: string; reason: string }>;
  costings?: CostingCalcProjection[];
}

interface ApiEnvelope {
  code?: number;
  message?: string;
  data?: SaveOutcome;
}

export interface UseCostingSaveResult {
  /** Queues one cell edit against a costing; edits accumulate until the next flush. */
  queueEdit: (costingId: string, edit: CostingEditPayload) => void;
  /** Sends every queued edit whose costing is not already in flight. */
  flush: () => void;
  /** Schedules a flush once the browser has finished moving focus. */
  scheduleFlush: () => void;
  /** Save status keyed by costing id; a costing absent from the map is idle. */
  saveState: Record<string, CostingSaveStatus>;
  /** True from the moment a request leaves until the database has confirmed it. */
  isSaving: boolean;
}

/**
 * Queues inline cell edits and sends them for server-side calculation when focus leaves a cell.
 * @param onCalculated called with the recomputed costings so the caller can repaint the grid
 * @param onSaveError called with the costing ids the server rejected
 * @param onSaved called once per successful batch, for aggregates derived from the saved set
 * @returns handles for queueing and flushing, plus per-costing save status
 */
export function useCostingSave(
  onCalculated?: (projections: CostingCalcProjection[]) => void,
  onSaveError?: (failedIds: string[]) => void,
  onSaved?: () => void,
): UseCostingSaveResult {
  const pendingRef = useRef<Map<string, CostingEditPayload[]>>(new Map());
  const inFlightRef = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const [saveState, setSaveState] = useState<Record<string, CostingSaveStatus>>({});
  const { t } = useTranslation('costing');
  /* Read through refs so sendBatch stays stable across language and callback changes. */
  const translateRef = useRef(t);
  translateRef.current = t;
  const onCalculatedRef = useRef(onCalculated);
  onCalculatedRef.current = onCalculated;

  const setStatus = useCallback((costingId: string, status: CostingSaveStatus | null) => {
    if (!isMountedRef.current) return;
    setSaveState((state) => {
      if (status === null) {
        if (!(costingId in state)) return state;
        const next = { ...state };
        delete next[costingId];
        return next;
      }
      if (state[costingId] === status) return state;
      return { ...state, [costingId]: status };
    });
  }, []);

  const sendBatch = useCallback(
    async (entries: Array<[string, CostingEditPayload[]]>) => {
      const ids = entries.map(([costingId]) => costingId);
      ids.forEach((costingId) => {
        inFlightRef.current.add(costingId);
        setStatus(costingId, 'saving');
      });

      try {
        const response = (await calculateCostingTodoService(entries.map(([costingId, edits]) => ({ costingId, edits })))) as ApiEnvelope;
        const failed = response?.data?.failed ?? [];
        const failedIds = new Set(failed.map((entry) => entry._id));

        ids.forEach((costingId) => setStatus(costingId, failedIds.has(costingId) ? 'error' : 'saved'));
        /* The response is the source of every figure on screen, so it is applied before anything
           else reacts to the save. */
        const projections = response?.data?.costings ?? [];
        if (projections.length > 0 && onCalculatedRef.current) onCalculatedRef.current(projections);
        /* Aggregates shown in the column header badges are derived from the saved set, so they go
           stale the moment a costing changes. */
        if (failedIds.size < ids.length && onSaved) onSaved();
        if (failed.length > 0) {
          toasterMsg(failed[0]?.reason || translateRef.current('COSTING_TODO.SAVE_PARTIAL_FAILED'), 'danger');
          if (onSaveError) onSaveError(Array.from(failedIds));
        }
      } catch {
        ids.forEach((costingId) => setStatus(costingId, 'error'));
        toasterMsg(translateRef.current('COSTING_TODO.SAVE_FAILED'), 'danger');
        if (onSaveError) onSaveError(ids);
      } finally {
        ids.forEach((costingId) => inFlightRef.current.delete(costingId));
        /* An edit made while the request was running is still queued, so send it now as one
           follow-up rather than one request per intervening keystroke. */
        if (pendingRef.current.size > 0) flushRef.current();
      }
    },
    [onSaveError, onSaved, setStatus],
  );

  const flush = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const ready = Array.from(pendingRef.current.entries()).filter(([costingId, edits]) => edits.length > 0 && !inFlightRef.current.has(costingId));
    if (ready.length === 0) return;
    ready.forEach(([costingId]) => pendingRef.current.delete(costingId));
    /* A fill-handle drag can touch more costings than one calculate request accepts, so the ready
       set goes out in request-sized chunks: the first leaves now, the rest follow one after another. */
    const chunks: Array<typeof ready> = [];
    for (let start = 0; start < ready.length; start += COSTING_CALC_CHUNK_SIZE) chunks.push(ready.slice(start, start + COSTING_CALC_CHUNK_SIZE));
    chunks.slice(1).reduce((previous, chunk) => previous.then(() => sendBatch(chunk)), sendBatch(chunks[0] ?? []));
  }, [sendBatch]);

  /* sendBatch's finally block needs the latest flush without making the two callbacks circular. */
  const flushRef = useRef(flush);
  flushRef.current = flush;

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    /* Deferred by a tick so the cell's own blur handler has committed its final value first. */
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      flushRef.current();
    }, BLUR_FLUSH_DELAY);
  }, []);

  const queueEdit = useCallback(
    (costingId: string, edit: CostingEditPayload) => {
      const queued = pendingRef.current.get(costingId) ?? [];
      /* Retyping into the same cell before a flush replaces the earlier value rather than sending
         both; two edits to one cell in one batch would just be applied in order for the same result,
         but the payload stays proportional to the cells touched. */
      const sameCell = queued.findIndex(
        (entry) =>
          entry.field === edit.field &&
          entry.dynamicColumn?.columnId === edit.dynamicColumn?.columnId &&
          entry.target?.colourwayId === edit.target?.colourwayId &&
          entry.target?.size === edit.target?.size &&
          entry.target?.lengthId === edit.target?.lengthId,
      );
      if (sameCell >= 0) queued[sameCell] = edit;
      else queued.push(edit);
      pendingRef.current.set(costingId, queued);
      setStatus(costingId, null);
    },
    [setStatus],
  );

  useEffect(() => {
    isMountedRef.current = true;
    /* Unlike the PIM autosave this page is modelled on, edits are not silently dropped when the tab
       closes: the last blur is flushed first. */
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (pendingRef.current.size === 0 && inFlightRef.current.size === 0) return;
      flushRef.current();
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      isMountedRef.current = false;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushRef.current();
    };
  }, []);

  /* Derived from saveState rather than a ref so the grid re-renders when it flips: every editable
     cell is disabled while a save is in flight, so an edit can never race the write it depends on. */
  const isSaving = Object.values(saveState).includes('saving');

  return { queueEdit, flush, scheduleFlush, saveState, isSaving };
}

/** The most costings one calculate request may carry — the server's MAX_COSTINGS_PER_REQUEST. */
const COSTING_CALC_CHUNK_SIZE = 50;
